import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { BackdropLoader, Icon, SimpleModal, toast } from '@livable/saas-react-components';
import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStore } from 'src/stores';
import { TemplateEditor } from './TemplateEditor';
import { scopeLabel } from './templateModel';
import useStyles from './styles';
const VIEW = {
  list: 'list',
  editor: 'editor'
};

/**
 * Manage the saved charge-export column mappings for one property: pick a default, edit, delete, or
 * build a new one.
 *
 * Controlled from the export button's menu rather than owning a trigger of its own — choosing a
 * template to export with is a one-click action in that menu, and this modal is only for the
 * occasional maintenance of them.
 */
export const ChargeExportTemplatesModal = observer(_ref => {
  let isOpen = _ref.isOpen,
    onClose = _ref.onClose,
    propertyId = _ref.propertyId,
    onTemplatesChanged = _ref.onTemplatesChanged;
  const classes = useStyles();
  const store = useStore();
  const _useState = useState(VIEW.list),
    _useState2 = _slicedToArray(_useState, 2),
    view = _useState2[0],
    setView = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    editing = _useState4[0],
    setEditing = _useState4[1];
  const templates = store.chargeExportTemplates.templatesFor(propertyId);
  const fields = store.chargeExportTemplates.fields;
  const isBusy = store.chargeExportTemplates.getTemplatesAsync.inProgress || store.chargeExportTemplates.getFieldsAsync.inProgress || store.chargeExportTemplates.saveTemplateAsync.inProgress || store.chargeExportTemplates.setDefaultTemplateAsync.inProgress || store.chargeExportTemplates.deleteTemplateAsync.inProgress;
  useEffect(() => {
    if (!isOpen) return;

    // Back to the list on every open: reopening after an edit should not drop the user mid-form.
    setView(VIEW.list);
    setEditing(null);
    store.chargeExportTemplates.getTemplatesAsync.run({
      propertyId
    });
    // The catalogue is process-wide and immutable, so it is fetched once per session.
    if (fields.length === 0) store.chargeExportTemplates.getFieldsAsync.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, propertyId]);
  const reload = async () => {
    await store.chargeExportTemplates.getTemplatesAsync.run({
      propertyId
    });
    if (onTemplatesChanged) onTemplatesChanged();
  };
  const handleSave = async values => {
    // The async model raises its own error toast and resolves to undefined on failure, so an
    // absent response is a failure that has already been reported.
    const resp = await store.chargeExportTemplates.saveTemplateAsync.run(values);
    if (!(resp !== null && resp !== void 0 && resp.response)) return;
    toast('success', {
      body: [values.templateId ? 'Template saved.' : 'Template created.']
    });
    await reload();
    setView(VIEW.list);
    setEditing(null);
  };
  const handleSetDefault = async template => {
    const resp = await store.chargeExportTemplates.setDefaultTemplateAsync.run({
      templateId: template.id
    });
    if (!(resp !== null && resp !== void 0 && resp.response)) return;
    await reload();
  };
  const handleDelete = async template => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete the template \"".concat(template.name, "\"?"))) return;
    await store.chargeExportTemplates.deleteTemplateAsync.run({
      templateId: template.id
    });
    if (store.chargeExportTemplates.deleteTemplateAsync.error) return;
    toast('success', {
      body: ['Template deleted.']
    });
    await reload();
  };
  const openEditor = template => {
    setEditing(template !== null && template !== void 0 ? template : null);
    setView(VIEW.editor);
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    handleClose: onClose,
    isOpen: isOpen,
    noFooter: true,
    title: "Export templates",
    subTitle: "Map Livable fields to the columns your PMS import expects",
    variant: "large"
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isBusy
  }), view === VIEW.list ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: classes.listHeader
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.listCount
  }, templates.length === 1 ? '1 template' : "".concat(templates.length, " templates")), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    onClick: () => openEditor(null)
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "plus"
  }), /*#__PURE__*/React.createElement("span", null, "New template"))), templates.length === 0 && !isBusy ? /*#__PURE__*/React.createElement("div", {
    className: classes.emptyState
  }, "No templates yet. Create one to export exactly the columns your PMS expects.") : null, templates.map(template => /*#__PURE__*/React.createElement("div", {
    className: classes.listRow,
    key: template.id
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.listRowMain
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.listRowName
  }, template.name), /*#__PURE__*/React.createElement("div", {
    className: classes.listRowMeta
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.badge
  }, scopeLabel(template)), template.isEffectiveDefault ? /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.badge, " ").concat(classes.badgeDefault)
  }, "Default") : null, /*#__PURE__*/React.createElement("span", null, template.columns.length === 1 ? '1 column' : "".concat(template.columns.length, " columns")))), /*#__PURE__*/React.createElement("div", {
    className: classes.listRowActions
  }, template.isEffectiveDefault ? null : /*#__PURE__*/React.createElement(Button, {
    className: classes.setDefaultButton,
    onClick: () => handleSetDefault(template)
  }, "Set as default"), /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    "aria-label": "edit template",
    onClick: () => openEditor(template)
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "edit"
  })), /*#__PURE__*/React.createElement(Button, {
    variant: "actionIcon",
    "aria-label": "delete template",
    onClick: () => handleDelete(template)
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "trash"
  })))))) : /*#__PURE__*/React.createElement(TemplateEditor, {
    fields: fields,
    template: editing,
    propertyId: propertyId,
    canScopeToProperty: Boolean(propertyId),
    isSaving: store.chargeExportTemplates.saveTemplateAsync.inProgress,
    onCancel: () => {
      setView(VIEW.list);
      setEditing(null);
    },
    onSave: handleSave
  }));
});
export default ChargeExportTemplatesModal;